import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import { Capacitor, PermissionState } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { TranslocoModule } from '@jsverse/transloco';

import { ReachStorage } from '~app-client/core/utils';
import { UiNewButtons } from '~app-ui-new/buttons';
import { BaseModalComponent, UiNewModals } from '~app-ui-new/modals';

const VALID_PERMISSIONS: PermissionState[] = ['prompt', 'prompt-with-rationale'];

const KEY = '__home-push-notifications-shown';
const MAX_DIFF = 1_000 * 60 * 60 * 24 * 7; // 7 days

ReachStorage.define(KEY, {
	version: 1,
	validate: (data): data is number => typeof data === 'number' && Number.isSafeInteger(data),
});

@Component({
	selector: 'push-notifications-popup',
	templateUrl: './push-notifications-popup.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [TranslocoModule, UiNewButtons, UiNewModals],
	host: {
		class: 'w-0 h-0 hidden  ',
	},
})
export class PushNotificationsPopupComponent implements OnInit {
	@ViewChild('baseModal')
	private readonly baseModal?: BaseModalComponent;

	@Output('finished')
	private readonly finished$$ = new EventEmitter<void>();

	async ngOnInit(): Promise<void> {
		const alreadyShownTimestamp = ReachStorage.getItem<number>(KEY) ?? Number.NaN;

		if (Number.isSafeInteger(alreadyShownTimestamp)) {
			const diff = Date.now() - alreadyShownTimestamp;
			if (diff < MAX_DIFF) {
				return this.markAsFinished();
			}
		}

		if (!Capacitor.isNativePlatform()) {
			return this.markAsFinished();
		}

		const permissions = await PushNotifications.checkPermissions();

		if (VALID_PERMISSIONS.includes(permissions.receive)) {
			this.openModal();
		} else {
			this.markAsFinished();
		}
	}

	public async requestPermission(): Promise<void> {
		try {
			this.baseModal?.close();
			await PushNotifications.requestPermissions();
		} catch (error) {
			console.error(error);
		} finally {
			this.markAsFinished();
		}
	}

	public _onModalClosed(): void {
		ReachStorage.setItem(KEY, Date.now());
	}

	private openModal(retryCount = 0): void {
		if (retryCount > 10) {
			this.markAsFinished();
			return;
		}

		if (!this.baseModal) {
			setTimeout(() => {
				this.openModal(retryCount + 1);
			}, 100);
			return;
		}

		this.baseModal.open();
	}

	private markAsFinished(): void {
		this.finished$$.emit();
	}
}
